import { useEffect, useRef, useState, type RefObject } from "react";
import mapboxgl, {
    type Map,
    type MapMouseEvent,
    type ExpressionSpecification,
} from "mapbox-gl";
import { useQuery } from "@tanstack/react-query";
import { Eye, EyeOff, MapPin, X } from "lucide-react";
import {
    postIsochrones,
    roundCoord,
    serializeAnchors,
    type Anchor,
    type TravelMode,
} from "@/repo/anchors";
import { ISOCHRONE_BAND_MINUTES } from "@/constants";
import type { TileEnv } from "@/env";
import "./Anchors.css";

const MODE_LABELS: {
    mode: TravelMode;
    label: string;
    title: string;
    disabled?: boolean;
}[] = [
    { mode: "driving", label: "D", title: "Driving" },
    {
        mode: "public_transport",
        label: "PT",
        title: "Public transport — not supported by the Mapbox isochrone provider",
        disabled: true,
    },
    { mode: "walking", label: "W", title: "Walking" },
];

const ISOCHRONE_SOURCE = "anchor-isochrones";

interface AnchorsProps {
    map: RefObject<Map | undefined>;
    anchors: Anchor[];
    /** Adding also appends the anchor to the stack rank — see Rank.tsx. */
    onAdd: (anchor: Anchor) => void;
    onUpdate: (index: number, anchor: Anchor) => void;
    /** Removing also drops/reindexes stack-rank references. */
    onRemove: (index: number) => void;
    env: TileEnv;
}

// One hue per anchor. Each anchor's bands share this hue and are distinguished
// by fill opacity (inner = stronger) and outline weight.
const ANCHOR_HUES: [number, number, number][] = [
    [230, 98, 184],
    [76, 68, 245],
    [68, 245, 82],
    [240, 247, 63],
    [178, 114, 230],
];

// Outlines carry most of the signal, so they get a touch more weight inwards.
const ISOCHRONE_BAND_LINE_WIDTH: Record<number, number> = {
    15: 1.2,
    25: 1,
    40: 0.7,
    60: 0.4,
};

const rgb = ([r, g, b]: [number, number, number]) => `rgb(${r}, ${g}, ${b})`;

export const anchorColour = (index: number) =>
    rgb(ANCHOR_HUES[index % ANCHOR_HUES.length]!);

// Build a Mapbox `match` expression keyed on the `minutes` band property.
function bandMatch(
    values: Record<number, string | number>,
): ExpressionSpecification {
    const cases = ISOCHRONE_BAND_MINUTES.flatMap((min) => [min, values[min]!]);
    const lastBand = ISOCHRONE_BAND_MINUTES[ISOCHRONE_BAND_MINUTES.length - 1]!;
    return ["match", ["get", "minutes"], ...cases, values[lastBand]!];
}

function Anchors(props: AnchorsProps) {
    const { map, anchors, onAdd, onUpdate, onRemove } = props;
    const [dropMode, setDropMode] = useState(false);
    // Ids of anchors whose isochrones are currently shown (off by default).
    const [visibleIso, setVisibleIso] = useState<Set<string>>(new Set());
    const markersRef = useRef<mapboxgl.Marker[]>([]);

    const serialized = serializeAnchors(anchors);

    const isochrones = useQuery({
        queryKey: ["isochrones", serialized, props.env],
        queryFn: () => postIsochrones(anchors),
        enabled: anchors.length > 0,
        staleTime: Infinity,
        retry: false,
    });

    // Drop mode: one-shot click handler that appends an anchor.
    useEffect(() => {
        const m = map.current;
        if (!m || !dropMode) return;

        m.getCanvas().style.cursor = "crosshair";
        const handleClick = (e: MapMouseEvent) => {
            onAdd({
                id: `a${anchors.length}`,
                lat: roundCoord(e.lngLat.lat),
                lng: roundCoord(e.lngLat.lng),
                modes: ["driving"],
            });
            setDropMode(false);
        };
        m.once("click", handleClick);

        return () => {
            m.off("click", handleClick);
            m.getCanvas().style.cursor = "";
        };
        // `onAdd` is included so the one-shot click handler always sees the
        // latest callback rather than a snapshot from when drop mode opened.
    }, [dropMode, map, onAdd]);

    // Draggable markers, recreated whenever the anchor set changes.
    useEffect(() => {
        const m = map.current;
        if (!m) return;

        markersRef.current = anchors.map((anchor, index) => {
            const element = document.createElement("div");
            element.className = "anchors-marker";
            element.style.background = anchorColour(index);
            const marker = new mapboxgl.Marker({
                element,
                draggable: true,
            })
                .setLngLat([anchor.lng, anchor.lat])
                .addTo(m);
            marker.on("dragend", () => {
                const pos = marker.getLngLat();
                onUpdate(index, {
                    ...anchor,
                    lat: roundCoord(pos.lat),
                    lng: roundCoord(pos.lng),
                });
            });
            return marker;
        });

        return () => {
            markersRef.current.forEach((marker) => marker.remove());
            markersRef.current = [];
        };
    }, [serialized]);

    // Isochrone overlay layers.
    useEffect(() => {
        const m = map.current;
        const data = isochrones.data;
        if (!m || !data) return;

        const shown = anchors
            .map((anchor, i) => ({ anchor, i }))
            .filter(({ anchor }) => visibleIso.has(anchor.id));
        if (shown.length === 0) return;

        m.addSource(ISOCHRONE_SOURCE, {
            type: "geojson",
            data: data.geojson,
        });

        const layerIds: string[] = [];
        const anchorFilter = (anchor: Anchor): ExpressionSpecification => [
            "==",
            ["get", "anchor_id"],
            anchor.id,
        ];

        // All faint fills first so no anchor's fill paints over another's
        // outline; the crisp rings then sit on top across every overlap.
        shown.forEach(({ anchor, i }) => {
            const id = `anchor-iso-fill-${i}`;
            m.addLayer({
                id,
                type: "fill",
                source: ISOCHRONE_SOURCE,
                filter: anchorFilter(anchor),
                paint: {
                    "fill-color": anchorColour(i),
                    "fill-opacity": 0.04,
                },
            });
            layerIds.push(id);
        });

        shown.forEach(({ anchor, i }) => {
            const id = `anchor-iso-line-${i}`;
            m.addLayer({
                id,
                type: "line",
                source: ISOCHRONE_SOURCE,
                filter: anchorFilter(anchor),
                paint: {
                    "line-color": anchorColour(i),
                    "line-width": bandMatch(ISOCHRONE_BAND_LINE_WIDTH),
                    "line-opacity": 0.6,
                },
            });
            layerIds.push(id);
        });

        return () => {
            for (const id of layerIds) {
                if (m.getLayer(id)) m.removeLayer(id);
            }
            if (m.getSource(ISOCHRONE_SOURCE)) m.removeSource(ISOCHRONE_SOURCE);
        };
    }, [isochrones.data, visibleIso]);

    function toggleMode(index: number, mode: TravelMode) {
        const anchor = anchors[index];
        if (!anchor) return;
        const has = anchor.modes.includes(mode);
        // Keep at least one mode enabled.
        if (has && anchor.modes.length === 1) return;
        onUpdate(index, {
            ...anchor,
            modes: has
                ? anchor.modes.filter((m) => m !== mode)
                : [...anchor.modes, mode],
        });
    }

    function removeAnchor(index: number) {
        onRemove(index);
    }

    function toggleIso(id: string) {
        setVisibleIso((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    return (
        <div className="anchors-section">
            <h4>Anchors</h4>
            <button
                className={
                    dropMode
                        ? "anchors-add-button anchors-add-button--active"
                        : "anchors-add-button"
                }
                onClick={() => setDropMode((d) => !d)}
            >
                <MapPin size={16} />
                {dropMode ? "Click the map to place…" : "Add anchor"}
            </button>

            <div className="control-panel-button-list">
                {anchors.map((anchor, index) => (
                    <div
                        key={anchor.id}
                        className="control-panel-button-list__item control-panel-button-list__item--active anchors-item"
                    >
                        <span
                            className="anchors-item__swatch"
                            style={{ background: anchorColour(index) }}
                        />
                        <span className="anchors-item__coords">
                            {anchor.lat.toFixed(4)}, {anchor.lng.toFixed(4)}
                        </span>
                        <span className="anchors-item__modes">
                            {MODE_LABELS.map(
                                ({ mode, label, title, disabled }) => (
                                    <button
                                        key={mode}
                                        title={title}
                                        disabled={disabled}
                                        className={
                                            anchor.modes.includes(mode)
                                                ? "anchors-mode-chip anchors-mode-chip--active"
                                                : "anchors-mode-chip"
                                        }
                                        onClick={() => toggleMode(index, mode)}
                                    >
                                        {label}
                                    </button>
                                ),
                            )}
                        </span>
                        <button
                            className={
                                visibleIso.has(anchor.id)
                                    ? "anchors-iso-toggle anchors-iso-toggle--active"
                                    : "anchors-iso-toggle"
                            }
                            title={
                                visibleIso.has(anchor.id)
                                    ? "Hide isochrone"
                                    : "Show isochrone"
                            }
                            onClick={() => toggleIso(anchor.id)}
                        >
                            {visibleIso.has(anchor.id) ? (
                                <Eye size={14} />
                            ) : (
                                <EyeOff size={14} />
                            )}
                        </button>
                        <button
                            className="control-panel-button-list__remove"
                            onClick={() => removeAnchor(index)}
                        >
                            <X size={16} />
                        </button>
                    </div>
                ))}
                {anchors.length === 0 && (
                    <div className="empty-state">
                        Drop anchors to weight scores by travel time.
                    </div>
                )}
            </div>

            {isochrones.isLoading && (
                <div className="anchors-status">Computing isochrones…</div>
            )}
            {isochrones.error && (
                <div className="anchors-status anchors-status--error">
                    {isochrones.error.message}
                </div>
            )}
            {isochrones.data && isochrones.data.failed.length > 0 && (
                <div className="anchors-status anchors-status--warning">
                    {isochrones.data.failed.length} isochrone(s) unavailable
                    (e.g. {isochrones.data.failed[0]?.mode}{" "}
                    {isochrones.data.failed[0]?.minutes}min)
                </div>
            )}
        </div>
    );
}

export default Anchors;
